import React, { useEffect, useState } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { useFormik } from "formik";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import CreditCardForm from "../components/CreditCardForm/CreditCardForm";
import { followCardRedirect } from "../utils/cardPayment";
import {
  payMoMoCheckout,
  payAirtelMoneyCheckout,
  payCardCheckout,
} from "../redux/createOrderAction";
import { apiCheckout } from "../api/ticketingApi";
import { PAYMENT_METHODS, isFwMomoMethod, fwNetworkFor } from "../config/market";
import { apiSetContactEmail } from "../api/profileApi";
import { fmtMoney } from "../components/Ticketing/format";

// Mobile-money entries come from the ACTIVE market registry (config/market.js):
// UG keeps MTN MoMo + Airtel; ZM adds Zamtel and routes ALL its mobile money
// over the Flutterwave rail. CARD is market-independent.
const METHODS = [
  ...PAYMENT_METHODS.filter((m) => m.rail === "MTN_DIRECT" || m.rail === "FW_MOMO").map(
    (m) => ({ key: m.key, label: m.label })
  ),
  { key: "CARD", label: "Card" },
];

const TicketCheckoutPage = () => {
  const { holdId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const holdFromStore = useSelector((s) => s.ticketing?.currentHold);
  const [hold] = useState(holdFromStore); // snapshot so a redux change can't blank the page mid-pay
  const currentUser = useSelector((s) => s.user?.currentUser);

  const clientId = currentUser?.result?._id || currentUser?._id;
  // Most Lusuku customers register with a PHONE NUMBER and have no email
  // (models/Users.js makes it optional + sparse). This used to fall back to "",
  // and Flutterwave's Joi schema rejects an empty string — the charge died
  // before it left the server and this screen rendered the raw SDK message
  // `validationError: "email" is not allowed to be empty` to the customer.
  //
  // Send it only when we actually have one. The backend
  // (services/payments/chargeIdentity.js) resolves a valid receipt address for
  // phone-only customers, so `undefined` here is correct and safe.
  const email =
    (currentUser?.email || currentUser?.result?.email || "").trim() || undefined;
  const name = currentUser?.name || currentUser?.fullName || currentUser?.result?.name || "";
  const prefillPhone =
    currentUser?.phoneNumber || currentUser?.result?.phoneNumber || currentUser?.phone || "";

  const [method, setMethod] = useState("MOMO");
  const [phone, setPhone] = useState(prefillPhone);
  // Optional email capture. Most customers register by phone and have no email,
  // so we ASK here — at the one moment the reason is concrete ("we'll email you
  // the ticket") and the customer is motivated. Never required: the platform
  // rule is ask, don't block, and seats are on a timer behind this screen.
  const [emailInput, setEmailInput] = useState("");
  const needsEmail = !email;
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [stage, setStage] = useState("form"); // form | processing | error
  const [message, setMessage] = useState("");

  const formik = useFormik({
    initialValues: { cardNumber: "", expiry_month: "", expiry_year: "", cardCvv: "", cardType: "", fullName: "" },
    onSubmit: () => {},
  });

  const valid = hold && String(hold._id) === String(holdId);

  useEffect(() => {
    if (!valid) return;
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.floor((new Date(hold.expiresAt).getTime() - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [valid, hold]);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const pay = async () => {
    if (!phone.trim()) {
      setMessage("Please enter a contact phone number.");
      return;
    }

    // If they filled the optional field, use it for THIS charge and save it to
    // the profile so future tickets and receipts reach them too. Deliberately
    // best-effort: a failure to save must never stop the payment.
    const typed = emailInput.trim();
    const effectiveEmail = email || typed || undefined;
    if (!email && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }
    if (method === "CARD" && !formik.values.cardNumber) {
      setMessage("Please enter your card details.");
      return;
    }
    setMessage("");
    setStage("processing");
    let orderId, amount;
    try {
      const res = await apiCheckout({ holdId, paymentMethod: method, phone: phone.trim() });
      orderId = res.orderId;
      amount = res.amount;
    } catch (err) {
      setStage("error");
      setMessage(err?.response?.data?.message || "Your reservation has expired. Please select again.");
      return;
    }

    const base = `${window.location.origin}/ticket-paid/${orderId}`;

    try {
      if (method === "MOMO" && !isFwMomoMethod("MOMO")) {
        // Uganda: direct MTN rail with in-page status polling.
        const action = await dispatch(
          payMoMoCheckout({
            mobile: phone.trim(),
            totalPrice: amount,
            client_id: clientId,
            orderItems: [],
            orderId,
          })
        );
        if (action.meta?.requestStatus === "fulfilled") {
          history.push(`/ticket-paid/${orderId}?rail=momo&clientId=${clientId}`);
        } else {
          // The server says what MTN answered and whether anything was charged
          // (services/payments/mtnInitiationOutcome.js on the dashboard server).
          // payMoMoCheckout rejects with the axios error, so the body is on
          // `response.data`.
          const body = action.payload?.response?.data || action.payload || {};
          setStage("error");
          setMessage(body.message || body.msg || "MoMo could not start the payment. Nothing was charged. Please try again.");
        }
      } else if (method !== "CARD") {
        // Flutterwave mobile money: UG Airtel, and ALL ZM networks
        // (MTN/Airtel/Zamtel — the registry supplies the network code).
        const action = await dispatch(
          payAirtelMoneyCheckout({
            mobile: phone.trim(),
            totalPrice: amount,
            client_id: clientId,
            email: effectiveEmail,
            uniqueTxRef: `${clientId}-${orderId}`,
            network: fwNetworkFor(method) || "airtel",
            redirect_url: `${base}?rail=airtel`,
            orderId,
          })
        );
        if (!action.error && action.payload?.data?.meta?.authorization) {
          followCardRedirect(action.payload.redirectUrl);
        } else {
          history.push(`/ticket-paid/${orderId}?rail=airtel`);
        }
      } else {
        const cardDetails = {
          cardNumber: formik.values.cardNumber,
          expiry_month: formik.values.expiry_month,
          expiry_year: formik.values.expiry_year,
          cardCvv: formik.values.cardCvv,
          cardType: formik.values.cardType,
          fullName: formik.values.fullName,
        };
        const action = await dispatch(
          payCardCheckout({
            cardDetails,
            totalPrice: amount,
            client_id: clientId,
            customer_name: name,
            email: effectiveEmail,
            network: formik.values.cardType || "card",
            redirect_url: `${base}?rail=card`,
            orderId,
          })
        );
        if (action.payload?.redirectUrl) {
          followCardRedirect(action.payload.redirectUrl);
        } else {
          history.push(`/ticket-paid/${orderId}?rail=card`);
        }
      }
    } catch (err) {
      setStage("error");
      setMessage("We couldn't start the payment. Please try again.");
    }
  };

  if (!valid) {
    return (
      <>
        <Navbar />
        <Page>
          <BackBar fallback="/events" />
          <Card>
            <h2>Reservation ended</h2>
            <Muted>Your ticket hold is no longer active. Please select again.</Muted>
            <Link to="/events"><Secondary>Back to events</Secondary></Link>
          </Card>
        </Page>
      </>
    );
  }

  const expired = secondsLeft === 0;

  return (
    <>
      <Navbar />
      <Page>
        <BackBar fallback="/events" />
        <Card>
          <h2>Checkout</h2>
          {!expired && secondsLeft != null && (
            <Timer $low={secondsLeft < 90}>Inventory held · {mmss(secondsLeft)} left</Timer>
          )}
          {expired && <Expired>Your hold expired. <Link to="/events">Select again</Link>.</Expired>}

          <Summary>
            {(hold.seats || []).length > 0 && (
              <Line><span>{hold.inventoryUnitLabel || "Reserved units"}</span><strong>{hold.seats.map((s) => s.seatLabel).join(", ")}</strong></Line>
            )}
            {(hold.gaItems || []).map((g) => (
              <Line key={g.ticketTypeKey}><span>{g.ticketTypeKey}{Number(g.admissionsPerUnit || 1) > 1 ? ` · ${g.admissionsPerUnit} guests each` : ""}</span><strong>× {g.qty}</strong></Line>
            ))}
            <TotalLine><span>Total</span><strong>{fmtMoney(hold.amount, hold.currency)}</strong></TotalLine>
          </Summary>

          {stage !== "processing" && !expired && (
            <>
              <Label>Pay with</Label>
              <Methods>
                {METHODS.map((m) => (
                  <MethodBtn key={m.key} $active={method === m.key} onClick={() => setMethod(m.key)}>
                    {m.label}
                  </MethodBtn>
                ))}
              </Methods>

              <Label>Contact phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07XXXXXXXX" inputMode="tel" />

              {needsEmail && (
                <>
                  <Label>Email <Optional>optional</Optional></Label>
                  <Input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="you@example.com"
                    inputMode="email"
                    autoComplete="email"
                    type="email"
                  />
                  <Hint>Add an email and we'll send your ticket there too. Either way it stays in My Tickets.</Hint>
                </>
              )}

              {method !== "CARD" && (
                <Hint>You'll approve the payment on this number{isFwMomoMethod(method) || method === "Airtel" ? " via a secure OTP page" : ""}.</Hint>
              )}

              {method === "CARD" && (
                <CardWrap>
                  <CreditCardForm
                    onFormChange={(data) => {
                      let em = null, ey = null;
                      if (typeof data.expirationDate === "string") [em, ey] = data.expirationDate.split("/");
                      formik.setValues({
                        cardNumber: data.cardNumber,
                        expiry_month: em,
                        expiry_year: ey,
                        cardCvv: data.cvv,
                        cardType: data.cardType,
                        fullName: data.fullName,
                      });
                    }}
                    values={formik.values}
                    errors={formik.errors}
                    touched={formik.touched}
                    handleBlur={formik.handleBlur}
                    handleChange={formik.handleChange}
                  />
                </CardWrap>
              )}

              {message && <Warn>{message}</Warn>}
              <Pay onClick={pay}>Pay {fmtMoney(hold.amount, hold.currency)}</Pay>
              <FinePrint>Your selected inventory stays held while you pay. Each QR states exactly how many guests it admits.</FinePrint>
            </>
          )}

          {stage === "processing" && (
            <Processing>
              <Spinner />
              <div>Starting your payment…</div>
              <Muted>Follow the prompt on your phone or the secure page. Don't close this window.</Muted>
            </Processing>
          )}

          {stage === "error" && (
            <>
              <Warn>{message}</Warn>
              <Pay onClick={() => setStage("form")}>Try again</Pay>
            </>
          )}
        </Card>
      </Page>
    </>
  );
};

const Page = styled.div`max-width:520px;margin:0 auto;padding:calc(var(--lusuku-nav-h,130px) + 16px) 16px 60px;min-height:60vh;`;
const Card = styled.div`background:#fff;border:1px solid #eef0f2;border-radius:16px;padding:24px;box-shadow:0 4px 20px rgba(0,0,0,.06);h2{font-size:20px;font-weight:800;color:#1f2937;margin:0 0 12px;}`;
const Muted = styled.div`color:#6b7280;font-size:13.5px;margin:6px 0;`;
const Timer = styled.div`display:inline-block;font-size:12px;font-weight:700;border-radius:20px;padding:5px 12px;margin-bottom:14px;background:${(p)=>p.$low?"#fef2f2":"#eaf5d6"};color:${(p)=>p.$low?"#dc2626":"#5f8f12"};`;
const Expired = styled.div`color:#dc2626;font-size:13.5px;margin-bottom:12px;`;
const Summary = styled.div`border:1px solid #f0f0f0;border-radius:12px;padding:14px 16px;margin:8px 0 18px;`;
const Line = styled.div`display:flex;justify-content:space-between;font-size:14px;color:#374151;padding:4px 0;`;
const TotalLine = styled(Line)`border-top:1px dashed #e5e7eb;margin-top:8px;padding-top:10px;font-size:16px;color:#1f2937;`;
const Optional = styled.span`font-weight:600;color:#9ca3af;text-transform:none;letter-spacing:0;font-size:11px;margin-left:6px;`;
const Label = styled.div`font-size:12px;font-weight:700;color:#6b7280;margin:14px 0 8px;text-transform:uppercase;letter-spacing:.4px;`;
const Methods = styled.div`display:flex;gap:8px;`;
const MethodBtn = styled.button`flex:1;padding:12px;border-radius:10px;font-size:13.5px;font-weight:600;cursor:pointer;border:1.6px solid ${(p)=>p.$active?"#83b524":"#e5e7eb"};background:${(p)=>p.$active?"#eaf5d6":"#fff"};color:${(p)=>p.$active?"#4d7a10":"#374151"};`;
const Input = styled.input`width:100%;padding:13px 14px;border:1.6px solid #e5e7eb;border-radius:10px;font-size:15px;&:focus{outline:none;border-color:#83b524;}`;
const Hint = styled.div`margin-top:8px;font-size:12.5px;color:#6b7280;`;
const CardWrap = styled.div`margin-top:14px;`;
const Warn = styled.div`margin-top:12px;color:#b45309;background:#fffbeb;border:1px solid #fde68a;padding:10px 12px;border-radius:8px;font-size:13px;`;
const Pay = styled.button`width:100%;margin-top:18px;background:#83b524;color:#fff;border:none;border-radius:12px;padding:15px;font-size:16px;font-weight:800;cursor:pointer;&:hover{background:#6f9e18;}`;
const FinePrint = styled.div`margin-top:12px;font-size:12px;color:#9ca3af;line-height:1.5;`;
const Processing = styled.div`text-align:center;padding:24px 0;color:#374151;font-weight:600;`;
const Spinner = styled.div`width:34px;height:34px;border-radius:50%;border:3px solid #e5e7eb;border-top-color:#83b524;margin:0 auto 14px;animation:spin .9s linear infinite;@keyframes spin{to{transform:rotate(360deg);}}`;
const Secondary = styled.div`display:inline-block;margin-top:14px;color:#4d7a10;font-weight:700;`;

export default TicketCheckoutPage;
